<template>
	<el-dialog
		title="发送给"
		:visible.sync="dialogVisible"
		width="600px"
		:before-close="handleClose"
		:modal-append-to-body="false"
		class="forward-message-dialog">
		<div class="dialog-content">
			<!-- 搜索框 -->
			<div class="search-box">
				<el-input
					v-model="searchKeyword"
					placeholder="搜索"
					prefix-icon="el-icon-search"
					clearable>
				</el-input>
			</div>

			<!-- 创建新的聊天 -->
			<div class="create-new-chat" @click="handleCreateNewChat">
				<span>创建新的聊天</span>
			</div>

			<!-- 最近聊天标题 -->
			<div class="section-title">最近聊天</div>

			<!-- 会话列表 -->
			<div class="chat-list">
				<div
					v-for="(chat, index) in filteredChats"
					:key="`chat-${chat.type}-${chat.targetId}-${index}`"
					class="chat-item"
					@click="toggleChatSelection(chat)">
					<el-checkbox
						:value="isSelected(chat.targetId)"
						@change="toggleChatSelection(chat)"
						@click.native.stop
						class="chat-checkbox">
					</el-checkbox>
					<div class="chat-info">
						<head-image
							:name="chat.showName"
							:size="40"
							:url="chat.headImage"
							:id="parseInt(chat.targetId) || 0">
						</head-image>
						<div class="chat-details">
							<span class="chat-name">{{ chat.showName }}</span>
							<span v-if="chat.type === 'GROUP'" class="member-count">({{ chat.memberCount || 0 }})</span>
						</div>
					</div>
				</div>
			</div>

			<!-- 转发提示 -->
			<div v-if="selectedChats.length > 0" class="forward-hint">
				[该条转发]共{{ messageCount }}条消息，将发送给{{ selectedChats.length }}个会话
			</div>

			<!-- 留言输入框 -->
			<div class="message-input">
				<el-input
					v-model="leaveMessage"
					type="textarea"
					:rows="3"
					placeholder="给朋友留言"
					maxlength="200"
					show-word-limit>
				</el-input>
			</div>
		</div>

		<!-- 底部按钮 -->
		<div slot="footer" class="dialog-footer">
			<el-button @click="handleClose">取消</el-button>
			<el-button type="primary" @click="handleForward" :disabled="selectedChats.length === 0">发送</el-button>
		</div>
	</el-dialog>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';

export default {
	name: 'ForwardMessageDialog',
	components: {
		HeadImage
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		messages: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			dialogVisible: false,
			searchKeyword: '',
			leaveMessage: '',
			selectedChatIds: [] // 存储选中的会话ID
		};
	},
	computed: {
		// 获取所有会话列表（过滤掉特殊会话）
		allChats() {
			const chats = this.$store.state.chatStore.chats || [];
			// 过滤掉智能体聚合会话、无效会话等
			return chats.filter(chat => {
				// 过滤掉智能体聚合会话
				if (chat.type === 'AGENT_AGGREGATE' || chat.targetId === '__AGENT_AGGREGATE__') {
					return false;
				}
				// 过滤掉没有targetId的会话
				if (!chat.targetId) {
					return false;
				}
				// 过滤掉没有名称的会话
				if (!chat.showName) {
					return false;
				}
				return true;
			});
		},
		// 过滤后的会话列表
		filteredChats() {
			if (!this.searchKeyword) {
				return this.allChats;
			}
			const keyword = this.searchKeyword.toLowerCase();
			return this.allChats.filter(chat => {
				return chat.showName && chat.showName.toLowerCase().includes(keyword);
			});
		},
		// 已选择的会话列表
		selectedChats() {
			return this.allChats.filter(chat => this.selectedChatIds.includes(chat.targetId));
		},
		// 消息数量
		messageCount() {
			return this.messages.length;
		}
	},
	watch: {
		visible(val) {
			this.dialogVisible = val;
			if (val) {
				this.resetDialog();
			}
		},
		dialogVisible(val) {
			this.$emit('update:visible', val);
		}
	},
	methods: {
		// 重置对话框状态
		resetDialog() {
			this.searchKeyword = '';
			this.leaveMessage = '';
			this.selectedChatIds = [];
		},
		// 判断会话是否被选中
		isSelected(chatId) {
			return this.selectedChatIds.includes(chatId);
		},
		// 切换会话选择状态
		toggleChatSelection(chat) {
			const index = this.selectedChatIds.indexOf(chat.targetId);
			if (index > -1) {
				this.selectedChatIds.splice(index, 1);
			} else {
				this.selectedChatIds.push(chat.targetId);
			}
		},
		// 创建新的聊天
		handleCreateNewChat() {
			this.$message.info('创建新的聊天功能待实现');
		},
		// 关闭对话框
		handleClose() {
			this.dialogVisible = false;
		},
		// 转发消息
		handleForward() {
			if (this.selectedChats.length === 0) {
				this.$message.warning('请选择要转发的会话');
				return;
			}

			this.$emit('forward', {
				targetChats: this.selectedChatIds,
				messages: this.messages,
				leaveMessage: this.leaveMessage
			});

			this.handleClose();
		}
	}
};
</script>

<style lang="scss" scoped>
.forward-message-dialog {
	::v-deep .el-dialog__body {
		padding: 20px;
		max-height: 600px;
		overflow-y: auto;
	}
}

.dialog-content {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.search-box {
	::v-deep .el-input__inner {
		border-radius: 20px;
		border: 2px solid #07c160;
		padding-left: 40px;
	}

	::v-deep .el-input__prefix {
		left: 12px;
	}
}

.create-new-chat {
	padding: 12px 16px;
	background: #f5f5f5;
	border-radius: 8px;
	text-align: center;
	cursor: pointer;
	transition: background 0.2s;

	&:hover {
		background: #e8e8e8;
	}

	span {
		font-size: 14px;
		color: #333;
	}
}

.section-title {
	font-size: 14px;
	color: #999;
	margin-top: 8px;
	text-align: left;
}

.chat-list {
	max-height: 300px;
	overflow-y: auto;
}

.chat-item {
	padding: 8px 0 8px 12px;
	cursor: pointer;
	transition: background 0.2s;
	display: flex;
	align-items: center;
	gap: 12px;

	&:hover {
		background: #f5f5f5;
	}

	.chat-checkbox {
		flex-shrink: 0;
		margin-right: 0;

		::v-deep .el-checkbox__inner {
			width: 18px;
			height: 18px;

			&::after {
				height: 9px;
				left: 6px;
			}
		}

		::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
			background-color: #07c160;
			border-color: #07c160;
		}
	}

	::v-deep .el-checkbox__label {
		display: none;
	}
}

.chat-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.chat-details {
	display: flex;
	align-items: center;
	gap: 4px;
}

.chat-name {
	font-size: 14px;
	color: #333;
}

.member-count {
	font-size: 12px;
	color: #999;
}

.forward-hint {
	padding: 8px 12px;
	background: #f0f0f0;
	border-radius: 4px;
	font-size: 13px;
	color: #666;
	text-align: center;
}

.message-input {
	::v-deep .el-textarea__inner {
		border-radius: 8px;
		resize: none;
	}
}

.dialog-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
}
</style>
